/**
 * competitionDb.ts
 * IndexedDB wrapper for offline-first storage of competition data.
 * Phase 3.1 of the Architecture Plan.
 */

const DB_NAME = 'squash_competition_db';
const DB_VERSION = 4;

export interface CompetitionData {
    version: string;
    updatedAt: string;
    poule: any;
    teams: any[];
    players: any[];
    schedule: any[];
    teamSchedules?: any[];
    standings?: any[];
    poulePlayerStats?: any[];
}

class CompetitionDB {
    private db: IDBDatabase | null = null;

    private async getDB(): Promise<IDBDatabase> {
        if (this.db) return this.db;

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onupgradeneeded = (event: any) => {
                const db = event.target.result;
                
                // Meta store (for version, currently selected poule, etc.)
                if (!db.objectStoreNames.contains('meta')) {
                    db.createObjectStore('meta');
                }
                
                // Poule info
                if (!db.objectStoreNames.contains('poule')) {
                    db.createObjectStore('poule', { keyPath: 'id' });
                }
                
                // Teams
                if (!db.objectStoreNames.contains('teams')) {
                    db.createObjectStore('teams', { keyPath: 'id' });
                }
                
                // Players (from teams in this poule)
                if (!db.objectStoreNames.contains('players')) {
                    db.createObjectStore('players', { keyPath: 'id' });
                }
                
                // Match Schedule
                if (!db.objectStoreNames.contains('schedule')) {
                    db.createObjectStore('schedule', { keyPath: 'id' });
                }
                
                // Enriched Player Data (Rankings cache)
                if (!db.objectStoreNames.contains('enriched')) {
                    db.createObjectStore('enriched', { keyPath: 'player_id' });
                }

                // Team Player Schedules (bijv. Hoenderdaal 1 speelschema per ronde)
                if (!db.objectStoreNames.contains('teamSchedules')) {
                    db.createObjectStore('teamSchedules', { keyPath: 'id', autoIncrement: true });
                }

                // Poule Standings (Stand van de teams binnen de poule)
                if (!db.objectStoreNames.contains('standings')) {
                    db.createObjectStore('standings', { keyPath: 'id', autoIncrement: true });
                }

                // Poule Player Stats (W/V per speler in de poule, van SBN draw)
                if (!db.objectStoreNames.contains('poulePlayerStats')) {
                    db.createObjectStore('poulePlayerStats', { keyPath: 'id', autoIncrement: true });
                }
            };
        });
    }

    async saveSyncData(data: CompetitionData) {
        const db = await this.getDB();
        const storeNames = ['meta', 'poule', 'teams', 'players', 'schedule'];
        if (db.objectStoreNames.contains('teamSchedules')) {
            storeNames.push('teamSchedules');
        }
        if (db.objectStoreNames.contains('standings')) {
            storeNames.push('standings');
        }
        if (db.objectStoreNames.contains('poulePlayerStats')) {
            storeNames.push('poulePlayerStats');
        }
        const tx = db.transaction(storeNames, 'readwrite');

        const metaStore = tx.objectStore('meta');
        const pouleStore = tx.objectStore('poule');
        const teamStore = tx.objectStore('teams');
        const playerStore = tx.objectStore('players');
        const scheduleStore = tx.objectStore('schedule');

        // Clean old data
        pouleStore.clear();
        teamStore.clear();
        playerStore.clear();
        scheduleStore.clear();

        if (db.objectStoreNames.contains('teamSchedules')) {
            const tsStore = tx.objectStore('teamSchedules');
            tsStore.clear();
            if (data.teamSchedules && Array.isArray(data.teamSchedules)) {
                data.teamSchedules.forEach(ts => tsStore.put(ts));
            }
        }

        if (db.objectStoreNames.contains('standings')) {
            const stStore = tx.objectStore('standings');
            stStore.clear();
            if (data.standings && Array.isArray(data.standings)) {
                data.standings.forEach(st => stStore.put(st));
            }
        }

        if (db.objectStoreNames.contains('poulePlayerStats')) {
            const ppsStore = tx.objectStore('poulePlayerStats');
            ppsStore.clear();
            if (data.poulePlayerStats && Array.isArray(data.poulePlayerStats)) {
                data.poulePlayerStats.forEach(pps => ppsStore.put(pps));
            }
        }

        // Save meta
        metaStore.put(data.version, 'data_version');
        metaStore.put(data.updatedAt, 'last_sync');
        metaStore.put(data.updatedAt, 'last_check');
        metaStore.put(data.poule.id, 'active_poule_id');

        // Save new data
        pouleStore.put(data.poule);
        data.teams.forEach(team => teamStore.put(team));
        data.players.forEach(player => playerStore.put(player));
        data.schedule.forEach(match => scheduleStore.put(match));

        return new Promise<void>((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    async setLastCheck(timestamp: string) {
        const db = await this.getDB();
        const tx = db.transaction(['meta'], 'readwrite');
        const store = tx.objectStore('meta');
        store.put(timestamp, 'last_check');
        return new Promise<void>((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    async getSyncStatus() {
        const db = await this.getDB();
        const tx = db.transaction(['meta'], 'readonly');
        const store = tx.objectStore('meta');
        
        const version = await this.getRequest(store.get('data_version'));
        const lastSync = await this.getRequest(store.get('last_sync'));
        const lastCheck = await this.getRequest(store.get('last_check'));
        const pouleId = await this.getRequest(store.get('active_poule_id'));

        return { version, lastSync, lastCheck, pouleId };
    }

    async getPouleInfo() {
        const db = await this.getDB();
        const tx = db.transaction(['poule'], 'readonly');
        const store = tx.objectStore('poule');
        const all = await this.getAllRequest(store.getAll());
        return all[0];
    }

    async getTeams() {
        const db = await this.getDB();
        const tx = db.transaction(['teams'], 'readonly');
        const store = tx.objectStore('teams');
        return this.getAllRequest(store.getAll());
    }

    async getPlayers() {
        const db = await this.getDB();
        const tx = db.transaction(['players'], 'readonly');
        const store = tx.objectStore('players');
        return this.getAllRequest(store.getAll());
    }

    async getSchedule() {
        const db = await this.getDB();
        const tx = db.transaction(['schedule'], 'readonly');
        const store = tx.objectStore('schedule');
        return this.getAllRequest(store.getAll());
    }

    async getTeamSchedules() {
        const db = await this.getDB();
        if (!db.objectStoreNames.contains('teamSchedules')) return [];
        const tx = db.transaction(['teamSchedules'], 'readonly');
        const store = tx.objectStore('teamSchedules');
        return this.getAllRequest(store.getAll());
    }

    async getStandings() {
        const db = await this.getDB();
        if (!db.objectStoreNames.contains('standings')) return [];
        const tx = db.transaction(['standings'], 'readonly');
        const store = tx.objectStore('standings');
        const res = await this.getAllRequest(store.getAll());
        return (res || []).sort((a: any, b: any) => (a.position || 0) - (b.position || 0));
    }

    async getPoulePlayerStats() {
        const db = await this.getDB();
        if (!db.objectStoreNames.contains('poulePlayerStats')) return [];
        const tx = db.transaction(['poulePlayerStats'], 'readonly');
        const store = tx.objectStore('poulePlayerStats');
        const res = await this.getAllRequest(store.getAll());
        return (res || []).sort((a: any, b: any) => (a.position || 0) - (b.position || 0));
    }

    async savePoulePlayerStats(stats: any[]) {
        if (!stats || !Array.isArray(stats)) return;
        const db = await this.getDB();
        if (!db.objectStoreNames.contains('poulePlayerStats')) return;
        const tx = db.transaction(['poulePlayerStats'], 'readwrite');
        const store = tx.objectStore('poulePlayerStats');
        store.clear();
        stats.forEach(s => store.put(s));
        return new Promise<void>((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    async cacheEnrichedPlayer(playerId: string, details: any) {
        const db = await this.getDB();
        const tx = db.transaction(['enriched'], 'readwrite');
        const store = tx.objectStore('enriched');
        store.put({ player_id: playerId, ...details, cached_at: Date.now() });
    }

    async getEnrichedPlayer(playerId: string) {
        const db = await this.getDB();
        const tx = db.transaction(['enriched'], 'readonly');
        const store = tx.objectStore('enriched');
        return this.getRequest(store.get(playerId));
    }

    private getRequest(request: IDBRequest): Promise<any> {
        return new Promise((resolve, reject) => {
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    private getAllRequest(request: IDBRequest): Promise<any[]> {
        return new Promise((resolve, reject) => {
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Vergelijkt server data met lokale IndexedDB en telt het aantal wijzigingen.
     * Telt: gewijzigde uitslagen, gewijzigde/nieuwe captains, speelsterkte wijzigingen.
     */
    async getPendingUpdatesCount(serverData: CompetitionData): Promise<number> {
        if (!serverData) return 0;
        let count = 0;

        try {
            // 1. Vergelijk spelers (captains, ratings, levels)
            if (serverData.players && serverData.players.length > 0) {
                const localPlayers = await this.getPlayers();
                const localPlayerMap = new Map<string, any>();
                localPlayers.forEach(p => {
                    const key = p.id || `${p.team_id}_${p.sbn_player_uuid || p.player_id}`;
                    localPlayerMap.set(String(key), p);
                    if (p.player_id) localPlayerMap.set(`${p.team_id}_${p.player_id}`, p);
                    if (p.sbn_player_uuid) localPlayerMap.set(`${p.team_id}_${p.sbn_player_uuid}`, p);
                });

                for (const sp of serverData.players) {
                    const key = sp.id || `${sp.team_id}_${sp.sbn_player_uuid || sp.player_id}`;
                    const lp = localPlayerMap.get(String(key)) ||
                               (sp.player_id ? localPlayerMap.get(`${sp.team_id}_${sp.player_id}`) : null) ||
                               (sp.sbn_player_uuid ? localPlayerMap.get(`${sp.team_id}_${sp.sbn_player_uuid}`) : null);

                    if (!lp) {
                        count++;
                    } else {
                        const localCaptain = Boolean(lp.isCaptain ?? lp.is_captain);
                        const serverCaptain = Boolean(sp.isCaptain ?? sp.is_captain);
                        const localLevel = lp.sbnPlayerLevel ?? lp.sbn_player_level ?? null;
                        const serverLevel = sp.sbnPlayerLevel ?? sp.sbn_player_level ?? null;
                        const localRank = lp.countryRanking ?? lp.country_rank ?? null;
                        const serverRank = sp.countryRanking ?? sp.country_rank ?? null;

                        if (localCaptain !== serverCaptain || String(localLevel) !== String(serverLevel) || String(localRank) !== String(serverRank)) {
                            count++;
                        }
                    }
                }
            }

            // 2. Vergelijk wedstrijduitslagen (scores)
            if (serverData.schedule && serverData.schedule.length > 0) {
                const localSchedule = await this.getSchedule();
                const localMatchMap = new Map<number, any>();
                localSchedule.forEach(m => {
                    if (m.id) localMatchMap.set(Number(m.id), m);
                });

                for (const sm of serverData.schedule) {
                    const lm = localMatchMap.get(Number(sm.id));
                    if (!lm) {
                        count++;
                    } else {
                        const homeDiff = (sm.home_score ?? null) !== (lm.home_score ?? null);
                        const awayDiff = (sm.away_score ?? null) !== (lm.away_score ?? null);
                        if (homeDiff || awayDiff) {
                            count++;
                        }
                    }
                }
            }
            // 3. Vergelijk standen (points, position, played)
            if (serverData.standings && serverData.standings.length > 0) {
                const localStandings = await this.getStandings();
                const localStandingsMap = new Map<number, any>();
                localStandings.forEach((st: any) => {
                    if (st.team_id) localStandingsMap.set(Number(st.team_id), st);
                });

                for (const ss of serverData.standings) {
                    const ls = localStandingsMap.get(Number(ss.team_id));
                    if (!ls) {
                        count++;
                    } else if (
                        Number(ss.points) !== Number(ls.points) ||
                        Number(ss.position) !== Number(ls.position) ||
                        Number(ss.played) !== Number(ls.played)
                    ) {
                        count++;
                    }
                }
            }
        } catch (err) {
            console.warn('[COMP DB] Fout bij berekenen pending updates count:', err);
        }

        return count;
    }

    async clearAll() {
        const db = await this.getDB();
        const storeNames = ['meta', 'poule', 'teams', 'players', 'schedule', 'enriched'];
        if (db.objectStoreNames.contains('teamSchedules')) storeNames.push('teamSchedules');
        if (db.objectStoreNames.contains('standings')) storeNames.push('standings');
        if (db.objectStoreNames.contains('poulePlayerStats')) storeNames.push('poulePlayerStats');
        const tx = db.transaction(storeNames, 'readwrite');
        storeNames.forEach(name => tx.objectStore(name).clear());
    }
}

export const competitionDb = new CompetitionDB();
